import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, hideDescription = true, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Defensive defaults: cap height at 90vh and allow scroll so any
        // long-content dialog (category managers, entity pickers, printer
        // lists, etc.) cannot overflow off-screen. Callers that manage
        // their own scroll region override with `max-h-*` and/or
        // `overflow-hidden` (tailwind-merge resolves both into the
        // caller's favour — `overflow-*` is a single conflict group).
        // iter822 → iter826 — cap height at 85dvh (dynamic viewport
        // height) so the dialog fits inside the *visible* iPad area even
        // when Safari's URL bar is shown. vh alone reports the layout
        // viewport (~950px on iPad portrait) but the visible area with
        // the URL bar is smaller (~810px) — vh-based caps overflow.
        // dvh tracks the real visible viewport. Falls back to vh on
        // older browsers via tailwind-merge → arbitrary value.
        "max-h-[85dvh] overflow-auto w-[95vw] sm:w-full",
        // iter829 — top-anchor the dialog instead of vertical-centering.
        // Centered dialogs on iPad Safari with tall content pushed off
        // the visible viewport because iOS mis-computes translate-y with
        // dynamic URL-bar heights. Anchoring at 4vh from top guarantees
        // the header + first row are always visible; long content
        // scrolls inside the dialog. Same left-centering as before.
        "fixed left-[50%] top-[4vh] z-50 grid max-w-lg translate-x-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}>
      {/* A11y: Radix Dialog emits a console warning when no
          DialogDescription is rendered. The vast majority of dialogs in
          this app only use a Title + custom body (no description prose),
          so we ship an empty sr-only DialogDescription as the default
          fallback. Pass `hideDescription={false}` on DialogContent if
          the caller wants to manage their own DialogDescription placement
          without this fallback. */}
      {hideDescription && (
        <DialogPrimitive.Description className="sr-only">
          Dialog content
        </DialogPrimitive.Description>
      )}
      {children}
      <DialogPrimitive.Close
        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
